import { FC, useState } from 'react'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import { ProductSize, TextLabel } from '@/network/graphql.g'
import { Box, Flex } from '@/components/atoms/Grid'
import { ArrowDownIconDefault } from '@/components/atoms/icons/ArrowDownIconDefault'
import ProductLabel from '@/components/molecules/productLabel'
import StickyBottomBar from '@/components/molecules/stickyBottomBar'
import ProductDetailStockDeliveryNew from '@/components/organisms/productDetailRedesign/productDetailStockDeliveryNew'

type ProductDetailStickyBarProps = {
  inlineButton: HTMLElement | null
  sizes: ProductSize[]
  selectedSize?: ProductSize
  onSelectSize: (sizeId: string) => void
  onAddToCart: () => Promise<void>
  price: string
  previousPrice?: string
  discountLabel?: TextLabel
}

// Mobile "add to cart" pinned to the bottom of the product detail.
const ProductDetailStickyBar: FC<ProductDetailStickyBarProps> = ({
  inlineButton,
  sizes,
  selectedSize,
  onSelectSize,
  onAddToCart,
  price,
  previousPrice,
  discountLabel
}) => {
  const { t } = useTranslation('common')
  const [isAdding, setIsAdding] = useState(false)

  const onClick = async () => {
    if (isAdding) {
      return
    }
    setIsAdding(true)
    try {
      await onAddToCart()
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <StickyBottomBar
      inlineButton={inlineButton}
      data-testid="productDetailStickyBar"
    >
      <StockRow>
        <ProductDetailStockDeliveryNew
          sizes={sizes}
          selectedSize={selectedSize}
        />
      </StockRow>
      <Flex alignItems="center">
        <PriceWrapper>
          {previousPrice || discountLabel ? (
            <Flex alignItems="center">
              {previousPrice ? (
                <PreviousPrice>{previousPrice}</PreviousPrice>
              ) : null}
              {discountLabel ? (
                <StyledProductLabel label={discountLabel} />
              ) : null}
            </Flex>
          ) : null}
          <Price>{price}</Price>
        </PriceWrapper>
        {selectedSize && sizes.length > 1 ? (
          <SizeSelectWrapper>
            <SizeSelect
              value={selectedSize.id}
              onChange={(e) => onSelectSize(e.target.value)}
              aria-label={t('ProductDetail.Sizes.chooseSize')}
              data-testid="productDetailStickyBarSize"
            >
              {sizes.map(({ id, value }) => (
                <option key={id} value={id}>
                  {value}
                </option>
              ))}
            </SizeSelect>
            <SizeSelectArrow width={10} height={10} />
          </SizeSelectWrapper>
        ) : null}
        <StyledButton
          type="button"
          onClick={onClick}
          disabled={isAdding}
          data-testid="productDetailStickyBarAddToCart"
        >
          {selectedSize ? (
            t('Products.Cart.add')
          ) : (
            <>
              <span>{t('ProductDetail.Sizes.chooseSize')}</span>
              <ButtonSubtitle>{t('Products.Cart.add')}</ButtonSubtitle>
            </>
          )}
        </StyledButton>
      </Flex>
    </StickyBottomBar>
  )
}

export default ProductDetailStickyBar

const StockRow = styled(Box)`
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 0.5px solid ${(props) => props.theme.colors.buttonBorderNew};

  &:empty {
    display: none;
  }
`

const PriceWrapper = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  margin-right: 8px;
`

const PreviousPrice = styled.span`
  margin-right: 6px;
  font-size: 12px;
  line-height: 1;
  text-decoration: line-through;
  color: ${(props) => props.theme.colors.infoSecondary};
`

const StyledProductLabel = styled(ProductLabel)`
  margin: 0;
`

const Price = styled.span`
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  color: ${(props) => props.theme.colors.accent};
`

const SizeSelectWrapper = styled(Box)`
  position: relative;
  flex-shrink: 0;
  margin-right: 8px;
`

const SizeSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  min-width: 64px;
  max-width: 96px;
  height: 48px;
  padding: 0 24px 0 12px;
  border: 0;
  border-radius: ${(props) => props.theme.buttonRadiusRadiusNew}px;
  box-shadow: inset 0 0 0 0.5px ${(props) => props.theme.colors.buttonBorderNew};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.buttonColorNew};
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  cursor: pointer;
`

const SizeSelectArrow = styled(ArrowDownIconDefault)`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  pointer-events: none;
`

const StyledButton = styled.button`
  flex: 1;
  min-width: 0;
  min-height: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 12px;
  border: 0;
  border-radius: ${(props) => props.theme.buttonRadiusRadiusNew}px;
  background-color: ${(props) => props.theme.colors.accentSecondary};
  color: ${(props) => props.theme.colors.background};
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const ButtonSubtitle = styled.span`
  font-size: 11px;
  font-weight: 400;
  line-height: 1;
  opacity: 0.9;
`
